import React, { useState, useEffect, useRef } from 'react';

import { RadioInput } from '../../../../shared-components/RadioInput/RadioInput';
import { StandardOrgSettingInput } from '../../../../shared-components/StandardSettingInput';
import { CtaButton } from '../../../../shared-components/CtaButton/CtaButton';
import { copyToClipboard } from '../../../../../common/utils';
import styles from '../../OrganizationSettings.module.css';
import LoginWorkflowComponent from './LoginWorkflowComponent';
import { Input } from 'admin/shared-components/Input/Input';
import { actions, useAppDispatch, useAppSelector } from 'redux-stores';

const SIGN_ON_URL = 'https://login.tigerconnect.com';
const LOGOUT_URL = 'https://login.tigerconnect.com';

const getMetadataErrorMessage = (validationError: string | null): string => {
  switch (validationError) {
    case 'emptyAuthSamlIspMetadataUrl':
      return 'ISP Metadata URL is required';
    case 'invalidAuthSamlIspMetadataUrl':
      return 'Invalid Metadata URL';
    default:
      return '';
  }
};

export const SamlComponent = () => {
  const dispatch = useAppDispatch();
  const [isCustomClaim, setIsCustomClaim] = useState(false);
  const [hasMetadataError, setHasMetadataError] = useState(false);

  // Track previous value of metadata URL
  const prevMetadataUrlRef = useRef<string | undefined>();

  // Get the current auth settings from state
  const {
    authSamlUrl,
    authSamlMetadataUrl,
    authSamlConsumeUrl,
    authSamlLogoutUrl,
    authSamlIspMetadataUrl,
  } = useAppSelector((state) => ({
    authSamlUrl: state.orgSettings.settings.authSamlUrl,
    authSamlMetadataUrl: state.orgSettings.settings.authSamlMetadataUrl,
    authSamlConsumeUrl: state.orgSettings.settings.authSamlConsumeUrl,
    authSamlLogoutUrl: state.orgSettings.settings.authSamlLogoutUrl,
    authSamlIspMetadataUrl: state.orgSettings.settings.authSamlIspMetadataUrl,
  }));

  const validationError = useAppSelector((state) => state.orgSettings.validationError);

  useEffect(() => {
    if (validationError && validationError.includes('AuthSamlIspMetadataUrl')) {
      setHasMetadataError(true);
      setTimeout(() => {
        const input = document.querySelector<HTMLInputElement>(
          'input[data-test-id="authSamlIspMetadataUrl input"]'
        );
        if (input) {
          input.focus();
          input.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [validationError]);

  // Clear error when user starts typing (value changes from previous)
  useEffect(() => {
    const prevValue = prevMetadataUrlRef.current;
    const hasValueChanged = prevValue !== authSamlIspMetadataUrl;

    if (hasMetadataError) {
      if (
        (validationError === 'emptyAuthSamlIspMetadataUrl' && authSamlIspMetadataUrl) ||
        (validationError === 'invalidAuthSamlIspMetadataUrl' &&
          hasValueChanged &&
          authSamlIspMetadataUrl !== prevValue)
      ) {
        setHasMetadataError(false);
        dispatch(actions.clearValidationError());
      }
    }

    // Update previous value for next comparison
    prevMetadataUrlRef.current = authSamlIspMetadataUrl;
  }, [authSamlIspMetadataUrl, hasMetadataError, validationError, dispatch]);

  return (
    <div>
      <LoginWorkflowComponent isOTPLoginDisabled={true} />
      <div className={styles.labelStyle}>
        <div className={styles.flexListStyleWithPadding}>
          <div className={styles.mainLabelStyle}>
            ISP Metadata URL <span style={{ color: 'red' }}>*</span>
          </div>
          <div className={`${styles.inputBoxStyle} ${hasMetadataError ? styles.inputError : ''}`}>
            <StandardOrgSettingInput settingName="authSamlIspMetadataUrl" type="input" />
          </div>
          {hasMetadataError && validationError && (
            <div className={styles.errorMessage}>{getMetadataErrorMessage(validationError)}</div>
          )}
        </div>
        <div className={styles.flexListStyleWithPadding}>
          <div className={styles.mainLabelStyle}>ISP Certificate Fingerprint</div>
          <div className={styles.inputBoxStyle}>
            <StandardOrgSettingInput settingName="authSamlIspFingerprint" type="input" />
          </div>
        </div>
        <div className={styles.flexListStyleWithPadding}>
          <div className={styles.labelStyle}>
            <div className={styles.mainLabelStyle}>Default Session Length</div>
            <div className={styles.sideLabelStyle}>
              Note: This is an optional field and only applicable if one is not provided by the ISP
            </div>
          </div>
          <div className={styles.inputBoxStyle}>
            <StandardOrgSettingInput settingName="authSamlTtl" type="input" />
          </div>
          <div className={styles.helpText}>Must be greater than 60</div>
        </div>
        <div className={styles.flexListStyleWithPadding}>
          <div className={styles.mainLabelStyle}>Assertion User ID</div>
          <RadioInput
            label={'Name ID'}
            name={'isCustomClaim'}
            onRadioClick={() => {
              setIsCustomClaim(false);
              dispatch(actions.updateSettings({ authSamlAssertionId: 'name_id' }));
            }}
            selectedValue={`${isCustomClaim}`}
            value={'false'}
          />
          <RadioInput
            label={'Custom Claim'}
            name={'isCustomClaim'}
            onRadioClick={() => {
              setIsCustomClaim(true);
              dispatch(actions.updateSettings({ authSamlAssertionId: '' }));
            }}
            selectedValue={`${isCustomClaim}`}
            value={'true'}
          />
          {isCustomClaim && (
            <div className={styles.inputBoxStyle}>
              <StandardOrgSettingInput settingName="authSamlAssertionId" type="input" />
            </div>
          )}
        </div>
        <div className={styles.flexListStyleWithPadding}>
          <div className={styles.mainLabelStyle}>TigerConnect ID</div>
          <StandardOrgSettingInput
            settingName="authSamlTcId"
            type="radio"
            options={[
              { label: 'Email Address', value: 'email' },
              { label: 'Username', value: 'username' },
              { label: 'Phone', value: 'phone' },
            ]}
          />
        </div>
        <div className={styles.inputBoxStyleWithCopyButton}>
          <div className={styles.mainLabelStyle}>Auth URL</div>
          <StandardOrgSettingInput settingName="authSamlUrl" type="input" disabled />
          <CtaButton
            label="Copy"
            size="small"
            disabled={!authSamlUrl}
            onClick={() => {
              if (authSamlUrl) copyToClipboard(authSamlUrl);
            }}
          />
        </div>
        <div className={styles.inputBoxStyleWithCopyButton}>
          <div className={styles.mainLabelStyle}>Metadata URL</div>
          <StandardOrgSettingInput settingName="authSamlMetadataUrl" type="input" disabled />
          <CtaButton
            label="Copy"
            size="small"
            disabled={!authSamlMetadataUrl}
            onClick={() => {
              if (authSamlMetadataUrl) copyToClipboard(authSamlMetadataUrl);
            }}
          />
        </div>
        <div className={styles.inputBoxStyleWithCopyButton}>
          <div className={styles.mainLabelStyle}>Consume URL</div>
          <StandardOrgSettingInput settingName="authSamlConsumeUrl" type="input" disabled />
          <CtaButton
            label="Copy"
            size="small"
            disabled={!authSamlConsumeUrl}
            onClick={() => {
              if (authSamlConsumeUrl) copyToClipboard(authSamlConsumeUrl);
            }}
          />
        </div>
        <div className={styles.inputBoxStyleWithCopyButton}>
          <div className={styles.mainLabelStyle}>Deauth URL</div>
          <StandardOrgSettingInput settingName="authSamlLogoutUrl" type="input" disabled />
          <CtaButton
            label="Copy"
            size="small"
            disabled={!authSamlLogoutUrl}
            onClick={() => {
              if (authSamlLogoutUrl) copyToClipboard(authSamlLogoutUrl);
            }}
          />
        </div>
        <div className={styles.flexListStyleWithPadding}>
          <div className={styles.mainLabelStyle}>Sign on URL</div>
          <Input value={SIGN_ON_URL} disabled customCSS={styles.inputBoxStyle} />
          <CtaButton
            label="Copy"
            size="small"
            disabled={!authSamlUrl}
            onClick={() => {
              copyToClipboard(SIGN_ON_URL);
            }}
          />
        </div>
        <div className={styles.flexListStyleWithPadding}>
          <div className={styles.mainLabelStyle}>Logout URL</div>
          <Input value={LOGOUT_URL} disabled customCSS={styles.inputBoxStyle} />
          <CtaButton
            label="Copy"
            size="small"
            disabled={!authSamlLogoutUrl}
            onClick={() => {
              copyToClipboard(LOGOUT_URL);
            }}
          />
        </div>
      </div>
    </div>
  );
};
